/**
 * @file ShopTextField.tsx
 * @description Текстовое поле форм раздела «Магазины» (phase19.28 этап А):
 * подпись, поле, подсказка или ошибка под полем — связаны через
 * `aria-describedby`, ошибка ставит `aria-invalid` (красная рамка темы).
 * Высоту и скругление задаёт тема (commora-console §7), не класс.
 */

import type { InputHTMLAttributes, ReactNode } from "react";

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { cn } from "@/lib/utils";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Подсказка под полем; при ошибке её место занимает ошибка. */
  hint?: ReactNode;
  error?: string;
  required?: boolean;
}

export function ShopTextField({ id, label, value, onChange, hint, error, required, className, ...rest }: Props) {
  const noteId = `${id}-note`;
  const note = error ?? hint;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id}>
        {label}
        {required ? <span aria-hidden> *</span> : null}
      </Label>
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={note ? noteId : undefined}
        {...rest}
      />
      {note ? (
        <p id={noteId} className={cn("type-caption", error ? "text-[var(--destructive-text)]" : "text-on-surface-variant")}>
          {note}
        </p>
      ) : null}
    </div>
  );
}
